import {
    MapViewportEventView,
    MapViewportUnitView,
    MapViewportHospitalView,
    SummaryCategoryEnum,
    SummaryCategoryEnumType,
    UnitStateGroupEnum,
} from '@mark43/cad-api';
import {
    AgencyTypeGlobal,
    agencyTypeIdToDisplay,
    globalAttributes,
} from '../../common/global-attributes';
import { ESRI_SPATIAL_REFERENCE } from '../configurations';
import { Circle, Graphic, Polygon } from '../constructors';
import { getEventIconType } from '../helpers/events';
import { getEventLocation, getMapViewportUnitLocation } from '../helpers/locations';
import { getUnitIconType } from '../helpers/units';
import { getMapIconTheme, getUnitStateId } from './map-icon-theme';
import GenericOptionSelected from '../images/integrated-map/generic-option-selected.png';
import GenericOption from '../images/integrated-map/generic-option.png';
import type {
    AmlLocationGeoPointEntity,
    CustomMapMarker,
    GeometryProperties,
    GeoPointExtended,
    GeoPointLocation,
    IconUiStates,
    IndexedAmlLocationEntity,
    MapViewportDerivedPolyline,
    MapViewportEventGraphicAttributes,
    MapViewportUnitGraphicAttributes,
    NearbyGeoCautionDtoExtended,
    PhoneCallGeoPointEntity,
    StationViewEntity,
} from '../types';
import { GeoPoint } from '../../location/types';
import { EsriMapEntityTypeEnum } from '../types';
import { colors } from '../../colors/colors';
import type { MappableSensor } from '../../sensors/types';
import { getCameraIconName } from '../../sensors/helpers';

/**
 * Helpers to create arcgis/esri Graphic object.
 */

/** Get the UI State of an icon.
 * Inactive means the icon is not being interacted with.
 * Selected means the icon is selected.
 * Summary means the icon belongs to the summary category that is selected.
 *
 * entityId is the id of the icon you are getting the UI state of.
 * entitySummaryCategory is the summary category the icon you are observing belongs to.
 * selectedEntityId is the id of an icon currently selected in the layer
 */
type GetUiStateProps = {
    entityId: number;
    entitySummaryCategory?: SummaryCategoryEnumType;
    entityType: EsriMapEntityTypeEnum;
    selectedEntityId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
const getUiState = ({
    entityId,
    selectedEntityId,
    entityType,
    selectedMapLedgerEntityType,
}: GetUiStateProps) => {
    let uiState: IconUiStates = 'Inactive';
    // check in order of precedence
    if (entityId === selectedEntityId && entityType === selectedMapLedgerEntityType) {
        uiState = 'Selected';
    }
    return uiState;
};

// ================================
// label graphic

const defaultLabelGraphicFont = {
    size: 10,
    family: 'Arial',
    weight: 'bold',
};

export type LabelFont = {
    size: number | string;
    weight?: string;
    family?: string;
    style?: string;
};
type CreateLabelGraphicParams = {
    point?: GeometryProperties;
    label?: string;
    yoffset?: number;
    font?: LabelFont;
};
const createLabelGraphic = ({
    point,
    label,
    yoffset = -3,
    font,
}: CreateLabelGraphicParams): Graphic | undefined => {
    if (!point || label === undefined) {
        return undefined;
    }
    const textSymbol = {
        type: 'text',
        color: colors.mapLabelText,
        haloColor: colors.mapLabelText,
        haloSize: 0,
        text: label,
        xoffset: 0,
        yoffset,
        font,
    };
    const labelPointGraphic = new Graphic({
        symbol: textSymbol,
        geometry: point,
    });
    labelPointGraphic.setAttribute('zIndex', 1);
    labelPointGraphic.setAttribute('tool', 'label');

    return labelPointGraphic;
};

// ================================
// Marker graphic

type CreatePointGraphicParam = {
    marker?: CustomMapMarker;
    selectedMarkerId?: string;
};
export const createPointGraphic = ({
    marker,
    selectedMarkerId,
}: CreatePointGraphicParam): {
    markerGraphic?: Graphic;
    labelGraphic?: Graphic;
} => {
    if (!marker) {
        return { markerGraphic: undefined, labelGraphic: undefined };
    }
    const geometry = {
        type: 'point',
        y: marker.location.lat,
        x: marker.location.lng,
        spatialReference: ESRI_SPATIAL_REFERENCE,
    };

    const symbol = {
        type: 'picture-marker',
        url: marker.id === selectedMarkerId ? GenericOptionSelected : GenericOption,
        width: 30,
        height: 38,
        color: 'black',
    };

    const markerGraphic = new Graphic({
        geometry,
        symbol,
        attributes: {
            id: marker.id,
            label: marker.label,
            isMarker: true,
            isLabel: false,
        },
    });

    const labelGraphic = createLabelGraphic({
        point: markerGraphic.geometry,
        label: marker.label,
        yoffset: 0,
        font: defaultLabelGraphicFont,
    });

    if (labelGraphic) {
        labelGraphic.attributes = {
            id: marker.id,
            label: marker.label,
            isMarker: true,
            isLabel: true,
        };
    }
    return { markerGraphic, labelGraphic };
};

// ================================
// GeoPoint Location (Point) graphic

type CreateGeoPointLocationGraphicParams = {
    objectId?: number;
    geoPointLocation?: GeoPointLocation;
    selectedGeoPointLocationId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
export const createGeoPointLocationGraphic = ({
    objectId,
    geoPointLocation,
    selectedGeoPointLocationId,
    selectedMapLedgerEntityType,
}: CreateGeoPointLocationGraphicParams): Graphic | undefined => {
    if (!geoPointLocation) {
        return undefined;
    }
    const uiState = getUiState({
        entityId: geoPointLocation.entityId,
        selectedEntityId: selectedGeoPointLocationId,
        selectedMapLedgerEntityType,
        entityType: EsriMapEntityTypeEnum.GEO_POINT_LOCATION,
    });
    const graphicProperties = {
        attributes: {
            objectId,
            entityId: geoPointLocation.entityId,
            iconName: `${uiState}`,
        },
        geometry: {
            type: 'point',
            y: geoPointLocation.latitude,
            x: geoPointLocation.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Station (Point) graphic

type CreateStationGraphicParams = {
    objectId?: number;
    stationViewEntity?: StationViewEntity;
    selectedStationId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
export const createStationGraphic = ({
    objectId,
    stationViewEntity,
    selectedStationId,
    selectedMapLedgerEntityType,
}: CreateStationGraphicParams): Graphic | undefined => {
    if (!stationViewEntity) {
        return undefined;
    }
    const { agencyTypeGlobalAttrId } = stationViewEntity.station;
    const entitySummaryCategory =
        agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.EMS
            ? SummaryCategoryEnum.EMS_STATIONS.name
            : SummaryCategoryEnum.FIRE_STATIONS.name;

    const uiState = getUiState({
        entitySummaryCategory,
        selectedMapLedgerEntityType,
        entityId: stationViewEntity.entityId,
        entityType: EsriMapEntityTypeEnum.STATION,
        selectedEntityId: selectedStationId,
    });
    const agencyType = agencyTypeIdToDisplay[agencyTypeGlobalAttrId as AgencyTypeGlobal].capitalize;

    const graphicProperties = {
        attributes: {
            objectId,
            stationName: stationViewEntity.station.name,
            entityId: stationViewEntity.entityId,
            uiState,
            iconName: `${agencyType}, ${uiState}`,
        },
        geometry: {
            type: 'point',
            y: stationViewEntity?.location?.latitude,
            x: stationViewEntity?.location?.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Hospital (Point) graphic

type CreateHospitalGraphicParams = {
    objectId?: number;
    mapViewportHospital?: MapViewportHospitalView;
    selectedHospitalId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
export const createHospitalGraphic = ({
    objectId,
    mapViewportHospital,
    selectedHospitalId,
    selectedMapLedgerEntityType,
}: CreateHospitalGraphicParams): Graphic | undefined => {
    if (!mapViewportHospital) {
        return undefined;
    }

    const uiState = getUiState({
        selectedMapLedgerEntityType,
        entityId: mapViewportHospital.entityId,
        entityType: EsriMapEntityTypeEnum.HOSPITAL,
        selectedEntityId: selectedHospitalId,
    });

    const graphicProperties = {
        attributes: {
            objectId,
            // hospitalName,
            entityId: mapViewportHospital.entityId,
            uiState,
            iconName: `${uiState}`,
        },
        geometry: {
            type: 'point',
            y: mapViewportHospital?.geoLocation?.latitude,
            x: mapViewportHospital?.geoLocation?.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Camera (Point) graphic

type CreateSensorGraphicParams = {
    objectId?: number;
    mappableSensor?: MappableSensor;
};

export const createSensorGraphic = ({
    objectId,
    mappableSensor,
}: CreateSensorGraphicParams): Graphic | undefined => {
    if (!mappableSensor) {
        return undefined;
    }

    const graphicProperties = {
        attributes: {
            objectId,
            entityId: mappableSensor.entityId,
            sensorId: mappableSensor.externalId,
            sensorName: mappableSensor.name,
            iconName: getCameraIconName(mappableSensor.online),
        },
        geometry: {
            type: 'point',
            y: mappableSensor.latitude,
            x: mappableSensor.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Unit (Point) graphic

type CreateUnitGraphicParams = {
    objectId?: number;
    mapViewportUnit?: MapViewportUnitView;
    selectedUnitId?: number;
    selectedUnitUserId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    showUnitLocationWhenGpsIsNotAvailable?: boolean;
};

export const getUnitIconNameAndUiState = ({
    mapViewportUnit,
    selectedUnitId,
    selectedUnitUserId,
    selectedMapLedgerEntityType,
}: {
    mapViewportUnit?: MapViewportUnitView;
    selectedUnitId?: number;
    selectedUnitUserId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
}): {
    iconName: string | undefined;
    uiState: IconUiStates | undefined;
} => {
    if (!mapViewportUnit) {
        return { iconName: undefined, uiState: undefined };
    }
    const isUser = mapViewportUnit.type === 'USER';
    const locationInaccurateStatus = mapViewportUnit.isLocationPotentiallyInaccurate
        ? 'Offline'
        : 'Online';
    const uiState = getUiState({
        selectedMapLedgerEntityType,
        entityId: mapViewportUnit.entityId,
        entitySummaryCategory: mapViewportUnit.summaryCategory,
        selectedEntityId: isUser ? selectedUnitUserId : selectedUnitId,
        entityType: isUser ? EsriMapEntityTypeEnum.USER : EsriMapEntityTypeEnum.UNIT,
    });
    const iconType = getUnitIconType(
        mapViewportUnit.unitStateGroup,
        mapViewportUnit.type,
        mapViewportUnit.summaryCategory
    );
    const isEnRouteAlternate =
        !isUser && mapViewportUnit.unitStateGroup === UnitStateGroupEnum.EN_ROUTE_ALTERNATE.name;

    const iconPieces: string[] = [
        mapViewportUnit.unitClass,
        iconType,
        locationInaccurateStatus,
        uiState,
        isEnRouteAlternate ? UnitStateGroupEnum.EN_ROUTE_ALTERNATE.name : undefined,
    ].filter((v) => !!v) as string[];

    const iconName = iconPieces.length
        ? getMapIconTheme().getUnitIconName(iconPieces.join(', '), getUnitStateId(mapViewportUnit))
        : undefined;
    return { iconName, uiState };
};

export const createUnitGraphic = ({
    objectId,
    mapViewportUnit,
    selectedUnitId,
    selectedUnitUserId,
    selectedMapLedgerEntityType,
    showUnitLocationWhenGpsIsNotAvailable,
}: CreateUnitGraphicParams): Graphic | undefined => {
    if (!mapViewportUnit) {
        return undefined;
    }

    const unitLocation = getMapViewportUnitLocation(
        mapViewportUnit,
        showUnitLocationWhenGpsIsNotAvailable
    );
    if (!unitLocation) {
        return undefined;
    }

    const { iconName, uiState } = getUnitIconNameAndUiState({
        mapViewportUnit,
        selectedUnitId,
        selectedUnitUserId,
        selectedMapLedgerEntityType,
    });

    if (!iconName || !uiState) {
        return undefined;
    }

    const callSignLabel =
        mapViewportUnit.type === 'UNIT'
            ? mapViewportUnit.callSign
            : mapViewportUnit?.personnelName
            ? `${mapViewportUnit.callSign ? `${mapViewportUnit.callSign} - ` : ''} ${
                  mapViewportUnit?.personnelName
              }`
            : mapViewportUnit.callSign;

    const unitGraphicAttributes: MapViewportUnitGraphicAttributes = {
        objectId,
        entityId: mapViewportUnit.entityId,
        callSign: callSignLabel,
        iconName,
        type: mapViewportUnit.type,
        uiState,
        eventPriorityString: mapViewportUnit.priority ? ` (${mapViewportUnit.priority})` : '',
        agencyTypeGlobalAttrId: mapViewportUnit.agencyTypeGlobalAttrId,
    };
    const graphicProperties = {
        attributes: unitGraphicAttributes,
        geometry: {
            type: 'point',
            y: unitLocation?.latitude,
            x: unitLocation?.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Event (Point) graphic

type CreateEventGraphicParams = {
    mapViewportEvent?: MapViewportEventView;
    objectId?: number;
    selectedEventId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
};
export const createEventGraphic = ({
    mapViewportEvent,
    objectId,
    selectedEventId,
    selectedMapLedgerEntityType,
}: CreateEventGraphicParams): Graphic | undefined => {
    if (!mapViewportEvent) {
        return undefined;
    }
    const eventLocation = getEventLocation(mapViewportEvent);
    if (!eventLocation) {
        return undefined;
    }
    const uiState = getUiState({
        selectedMapLedgerEntityType,
        entityType: EsriMapEntityTypeEnum.EVENT,
        entityId: mapViewportEvent.entityId,
        entitySummaryCategory: mapViewportEvent.summaryCategory,
        selectedEntityId: selectedEventId,
    });
    const iconType = getEventIconType(mapViewportEvent.summaryCategory);
    const eventGraphicAttributes: MapViewportEventGraphicAttributes = {
        objectId,
        entityId: mapViewportEvent.entityId,
        iconName: getMapIconTheme().getEventIconName(
            `${iconType}, ${uiState}`,
            mapViewportEvent.priorityAttributeId,
            mapViewportEvent.agencyTypeGlobalAttrId
        ),
        uiState,
        priority: mapViewportEvent.priority,
        agencyTypeGlobalAttrId: mapViewportEvent.agencyTypeGlobalAttrId,
    };
    const graphicProperties = {
        attributes: eventGraphicAttributes,
        geometry: {
            type: 'point',
            y: eventLocation.latitude,
            x: eventLocation.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Phone Call (E911 ANI/ALI) graphic

type CreatePhoneCallGraphicParams = {
    objectId?: number;
    selectedPhoneCallId?: number;
    phoneCallGeoPointEntity?: PhoneCallGeoPointEntity;
};
export const createPhoneCallGraphic = ({
    objectId,
    selectedPhoneCallId,
    phoneCallGeoPointEntity,
}: CreatePhoneCallGraphicParams): Graphic | undefined => {
    if (!phoneCallGeoPointEntity) {
        return undefined;
    }
    const iconName =
        phoneCallGeoPointEntity.entityId === selectedPhoneCallId
            ? 'e911_selected'
            : 'e911_inactive';
    const graphicProperties = {
        attributes: {
            objectId,
            iconName,
            entityId: phoneCallGeoPointEntity.entityId,
        },
        geometry: {
            type: 'point',
            y: phoneCallGeoPointEntity.latitude,
            x: phoneCallGeoPointEntity.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// AML (RapidSOS) graphic

type CreateAmlGraphicParams = {
    objectId?: number;
    selectedAmlPhoneNumberId?: number;
    amlLocationGeoPointEntity?: AmlLocationGeoPointEntity;
};
export const createAmlGraphic = ({
    objectId,
    amlLocationGeoPointEntity,
    selectedAmlPhoneNumberId,
}: CreateAmlGraphicParams): Graphic | undefined => {
    if (!amlLocationGeoPointEntity) {
        return undefined;
    }
    const iconName = selectedAmlPhoneNumberId ? 'rapidsos_selected' : 'rapidsos_inactive';
    const graphicProperties = {
        attributes: {
            objectId,
            iconName,
            entityId: amlLocationGeoPointEntity.entityId,
            label: 'RapidSOS',
            amlPhoneCallId: amlLocationGeoPointEntity.amlPhoneCallId,
        },
        geometry: {
            type: 'point',
            y: amlLocationGeoPointEntity.latitude,
            x: amlLocationGeoPointEntity.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

type CreateAmlHistoryGraphicParams = {
    objectId?: number;
    indexedAmlLocationEntity?: IndexedAmlLocationEntity;
};
export const createAmlHistoryGraphic = ({
    objectId,
    indexedAmlLocationEntity,
}: CreateAmlHistoryGraphicParams): Graphic | undefined => {
    if (!indexedAmlLocationEntity) {
        return undefined;
    }
    const graphicProperties = {
        attributes: {
            objectId,
            entityId: indexedAmlLocationEntity.entityId,
            iconName: 'rapidsos_previous',
            index: indexedAmlLocationEntity.index,
        },
        geometry: {
            type: 'point',
            y: indexedAmlLocationEntity.latitude,
            x: indexedAmlLocationEntity.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Polyline graphic

type CreatePolylineGraphicParams = {
    objectId?: number;
    mapViewportPolyline?: MapViewportDerivedPolyline;
};
export const createPolylineGraphic = ({
    objectId,
    mapViewportPolyline,
}: CreatePolylineGraphicParams): Graphic | undefined => {
    if (!mapViewportPolyline) {
        return undefined;
    }
    // needs to be in format of number array https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Polyline.html#paths
    const graphicProperties = {
        attributes: {
            objectId,
            entityId: mapViewportPolyline.entityId,
            type: mapViewportPolyline.type,
        },
        geometry: {
            type: 'polyline',
            spatialReference: ESRI_SPATIAL_REFERENCE,
            paths: [
                [mapViewportPolyline.path[0].longitude, mapViewportPolyline.path[0].latitude],
                [mapViewportPolyline.path[1].longitude, mapViewportPolyline.path[1].latitude],
            ],
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Circle graphic

type CreateCircleGraphicParams = {
    center?: GeoPoint;
    radius?: number;
    entityId?: number;
    objectId?: number;
    shouldShowRadius?: boolean;
};
export const createCircleGraphic = ({
    center,
    radius,
    entityId,
    objectId,
    shouldShowRadius,
}: CreateCircleGraphicParams): Graphic | undefined => {
    if (!center || radius === undefined) {
        return undefined;
    }
    if (entityId) {
        // when entityId is provided, these circle usually need to be re-drawn
        const graphicProperties = {
            attributes: {
                objectId,
                entityId,
            },
            geometry: new Circle({
                center,
                radius: shouldShowRadius ? radius : 0,
                radiusUnit: 'meters',
                geodesic: true,
                numberOfPoints: 100,
            }),
        };
        return new Graphic(graphicProperties);
    } else {
        // mainly used for embedded map for one-time display
        const graphicProperties = {
            geometry: new Circle({
                center,
                radius,
            }),
            symbol: {
                type: 'simple-fill',
                color: [...colors.esriColors.circleFill],
                outline: {
                    color: [...colors.esriColors.circleOutline],
                    width: 1,
                },
            },
        };
        return new Graphic(graphicProperties);
    }
};

// ================================
// Polygon graphic

type CreatePolygonGraphicParams = {
    coordinates?: number[][];
};
export const createPolygonGraphic = ({
    coordinates,
}: CreatePolygonGraphicParams): Graphic | undefined => {
    if (!coordinates?.length) {
        return undefined;
    }
    const polygon = new Polygon({
        rings: [coordinates],
        spatialReference: ESRI_SPATIAL_REFERENCE,
    });
    const graphicProperties = {
        geometry: polygon,
        symbol: {
            type: 'simple-fill',
            color: [...colors.esriColors.polygonFill],
            outline: {
                color: [...colors.esriColors.polygonOutline],
                width: 1,
            },
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Nearby Geo Cautions graphic

type CreateNearbyGeoCautionsGraphicParams = {
    objectId?: number;
    nearbyGeoCaution?: NearbyGeoCautionDtoExtended;
    selectedNearbyGeoCautionCoordinates?: GeoPoint;
};
export const createNearbyGeoCautionsGraphic = ({
    objectId,
    nearbyGeoCaution,
    selectedNearbyGeoCautionCoordinates,
}: CreateNearbyGeoCautionsGraphicParams): Graphic | undefined => {
    if (!nearbyGeoCaution) {
        return undefined;
    }
    const iconName =
        nearbyGeoCaution.latitude === selectedNearbyGeoCautionCoordinates?.latitude &&
        nearbyGeoCaution.longitude === selectedNearbyGeoCautionCoordinates?.longitude
            ? 'geo_caution_focus'
            : 'geo_caution_default';
    const graphicProperties = {
        attributes: {
            objectId,
            entityId: nearbyGeoCaution.entityId,
            iconName,
        },
        geometry: {
            type: 'point',
            y: nearbyGeoCaution.latitude,
            x: nearbyGeoCaution.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Pin Initial Click Geo Point graphic

type CreateInitialGeoPointPinGraphicParams = {
    objectId?: number;
    geoPoint?: GeoPointExtended;
};
export const createInitialGeoPointPinGraphic = ({
    objectId,
    geoPoint,
}: CreateInitialGeoPointPinGraphicParams): Graphic | undefined => {
    if (!geoPoint) {
        return undefined;
    }

    const graphicProperties = {
        attributes: {
            objectId,
            entityId: geoPoint.entityId,
        },
        geometry: {
            type: 'point',
            y: geoPoint.latitude,
            x: geoPoint.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };
    return new Graphic(graphicProperties);
};

// ================================
// Map entity icon graphic

type CreateEntityIconGraphicParams = {
    center?: GeoPoint;
    url: string;
    width?: string;
    height?: string;
    color?: string;
};
export const createEntityIconGraphic = ({
    center,
    url,
    width = '32px',
    height = '32px',
    color = '',
}: CreateEntityIconGraphicParams): Graphic | undefined => {
    if (!center) {
        return undefined;
    }

    const graphicProperties = {
        symbol: {
            type: 'picture-marker',
            url,
            width,
            height,
            color,
        },
        geometry: {
            type: 'point',
            y: center.latitude,
            x: center.longitude,
            spatialReference: ESRI_SPATIAL_REFERENCE,
        },
    };

    return new Graphic(graphicProperties);
};

// ================================
// Map entity label graphic

type CreateEntityLabelGraphic = {
    center?: GeoPoint;
    label: string;
    font: LabelFont;
    yoffset?: number;
};
export const createEntityLabelGraphic = ({
    center,
    label,
    font,
    yoffset,
}: CreateEntityLabelGraphic): Graphic | undefined => {
    if (!center) {
        return undefined;
    }

    const geometry = {
        type: 'point',
        y: center.latitude,
        x: center.longitude,
        spatialReference: ESRI_SPATIAL_REFERENCE,
    };

    const labelGraphic = createLabelGraphic({
        point: geometry,
        label,
        font,
        yoffset,
    });

    return labelGraphic;
};
